import { useEffect, useMemo, useState } from "react";
import { X, Fuel, Loader2, LineChart as LineChartIcon, Eye, EyeOff, TrendingDown } from "lucide-react";
import BrandLoader from "@/components/BrandLoader";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceDot } from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { limitDays } from "@/lib/reportLimit";

const PERIODS = [{ h: 24, label: "24 h" }, { h: 168, label: "7 j" }, { h: 720, label: "30 j" }];
const CONF_LABEL = { eleve: "élevée", moyen: "moyenne", faible: "faible" };
const CONF_COLOR = { eleve: "#22C55E", moyen: "#F5821F", faible: "#94A3B8" };
const STATUS_MSG = {
  not_compatible: "Donnée CAN carburant non disponible sur ce véhicule.",
  insufficient: "Signal carburant insuffisant sur la période.",
  electric_excluded: "Véhicule électrique — le niveau carburant ne s'applique pas.",
};

export default function FuelChartModal({ vehicle, onClose }) {
  const { user } = useAuth();
  const lim = limitDays(user);
  const canSeeRaw = ["admin", "technicien", "gestionnaire"].includes(user?.role);
  const [hours, setHours] = useState(24);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get(`/vehicles/${vehicle.id}/fuel-series?hours=${hours}`)
      .then((r) => { if (!cancel) setData(r.data); })
      .catch(() => { if (!cancel) setData({ status: "not_compatible", points: [], refuel_events: [], drop_events: [] }); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [vehicle.id, hours]);

  const tz = data?.timezone || "Pacific/Noumea";
  const points = useMemo(() => (data?.points || []).map((p) => ({
    t: p.timestamp, raw: p.raw_pct, filtered: p.filtered_pct,
  })), [data]);
  const events = data?.refuel_events || [];
  const drops = data?.drop_events || [];
  const DROP_COLOR = "#EF4444";

  const fmtX = (t) => {
    try {
      const d = new Date(t);
      return hours <= 24
        ? d.toLocaleTimeString("fr-FR", { timeZone: tz, hour: "2-digit", minute: "2-digit" })
        : d.toLocaleDateString("fr-FR", { timeZone: tz, day: "2-digit", month: "2-digit" });
    } catch { return ""; }
  };
  const fmtFull = (t) => { try { return new Date(t).toLocaleString("fr-FR", { timeZone: tz, day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return t; } };

  return (
    <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="fuel-chart-modal">
      <div className="glass rounded-xl w-full max-w-2xl p-5 animate-slidein border border-[#22C55E]/40" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <Fuel className="w-5 h-5 shrink-0" style={{ color: "#22C55E" }} />
            <div className="min-w-0">
              <p className="font-bold text-base truncate">{vehicle.name} <span className="data text-xs text-muted-foreground">· {vehicle.plate}</span></p>
              <p className="label-mono">Niveau carburant &amp; ravitaillements</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="fuel-chart-close" className="text-muted-foreground hover:text-foreground p-1.5 rounded-md hover:bg-white/10"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          {canSeeRaw ? (
            <button onClick={() => setShowRaw((s) => !s)} data-testid="fuel-toggle-raw"
              className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold border transition-colors border-border text-muted-foreground hover:text-foreground">
              {showRaw ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />} Donnée brute
            </button>
          ) : <span />}
          <div className="flex gap-1" data-testid="fuel-period-tabs">
            {PERIODS.filter((p) => !lim || p.h <= lim * 24).map((p) => (
              <button key={p.h} onClick={() => setHours(p.h)} data-testid={`fuel-period-${p.h}`}
                className={`text-[11px] font-semibold px-2.5 py-1 rounded-md border transition-colors ${hours === p.h ? "bg-[#22C55E] text-white border-[#22C55E]" : "border-border text-muted-foreground hover:text-foreground"}`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="h-72 w-full" data-testid="fuel-chart-canvas">
          {loading ? (
            <div className="h-full flex items-center justify-center"><BrandLoader size={44} rings={false} label="Analyse du carburant…" /></div>
          ) : data && data.status !== "ok" ? (
            <div className="h-full flex items-center justify-center text-muted-foreground text-sm text-center px-6" data-testid="fuel-chart-status">{STATUS_MSG[data.status] || "Signal indisponible."}</div>
          ) : points.length < 2 ? (
            <div className="h-full flex items-center justify-center text-muted-foreground text-sm" data-testid="fuel-chart-empty">Pas assez de données sur la période.</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={points} margin={{ top: 8, right: 16, left: -8, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis dataKey="t" tickFormatter={fmtX} tick={{ fontSize: 11, fill: "#a1a1aa" }} minTickGap={40} />
                <YAxis tick={{ fontSize: 11, fill: "#a1a1aa" }} width={44} domain={[0, 100]} />
                <Tooltip
                  contentStyle={{ background: "#18181b", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, fontSize: 12 }}
                  labelFormatter={fmtFull}
                  formatter={(v, n) => [`${v} %`, n === "raw" ? "Donnée brute" : "Niveau filtré"]} />
                {showRaw && <Line type="monotone" dataKey="raw" stroke="#64748B" strokeWidth={1} strokeDasharray="4 3" dot={false} isAnimationActive={false} />}
                <Line type="monotone" dataKey="filtered" stroke="#22C55E" strokeWidth={2} dot={false} isAnimationActive={false} />
                {events.map((e, i) => (
                  <ReferenceDot key={i} x={e.timestamp} y={e.after_pct} r={6} fill={CONF_COLOR[e.confidence] || "#F5821F"} stroke="#0b1220" strokeWidth={1.5} isFront />
                ))}
                {drops.map((e, i) => (
                  <ReferenceDot key={`d${i}`} x={e.detected_at} y={e.after_pct} r={6} fill={DROP_COLOR} stroke="#0b1220" strokeWidth={1.5} isFront />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {data?.status === "ok" && (
          <div className="mt-3" data-testid="fuel-refuel-list">
            <p className="label-mono mb-1">⛽ Ravitaillements probables · {events.length}</p>
            {events.length === 0 ? (
              <p className="text-xs text-muted-foreground">Aucun ravitaillement probable détecté sur la période.</p>
            ) : (
              <div className="space-y-1 max-h-28 overflow-auto pr-1">
                {events.map((e, i) => (
                  <div key={i} data-testid={`fuel-refuel-${i}`} className="flex items-center justify-between text-xs bg-white/5 rounded-md px-2.5 py-1.5">
                    <span className="data">{fmtFull(e.timestamp)}</span>
                    <span className="font-semibold">{e.before_pct}% → {e.after_pct}% <span style={{ color: "#22C55E" }}>(+{e.delta_pct})</span></span>
                    <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ color: CONF_COLOR[e.confidence], background: `${CONF_COLOR[e.confidence]}22` }}>{CONF_LABEL[e.confidence] || e.confidence}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {data?.status === "ok" && (
          <div className="mt-3" data-testid="fuel-drop-list">
            <p className="label-mono mb-1 flex items-center gap-1"><TrendingDown className="w-3.5 h-3.5" style={{ color: DROP_COLOR }} /> Baisses anormales probables · {drops.length}</p>
            {drops.length === 0 ? (
              <p className="text-xs text-muted-foreground">Aucune baisse anormale probable détectée sur la période.</p>
            ) : (
              <div className="space-y-1 max-h-28 overflow-auto pr-1">
                {drops.map((e, i) => (
                  <div key={i} data-testid={`fuel-drop-${i}`} className="flex items-center justify-between gap-2 text-xs bg-white/5 rounded-md px-2.5 py-1.5">
                    <span className="data truncate">
                      {e.uncertain_time
                        ? `Constatée entre ${fmtFull(e.window_start)} et ${fmtFull(e.window_end)}`
                        : fmtFull(e.detected_at)}
                    </span>
                    <span className="font-semibold whitespace-nowrap">{e.before_pct}% → {e.after_pct}% <span style={{ color: DROP_COLOR }}>({e.delta_pct})</span></span>
                    <span className="rounded-full px-2 py-0.5 text-[10px] font-bold whitespace-nowrap" style={{ color: CONF_COLOR[e.confidence], background: `${CONF_COLOR[e.confidence]}22` }}>{CONF_LABEL[e.confidence] || e.confidence}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <p className="text-[10px] text-muted-foreground mt-2 text-right">Niveau filtré (médiane + lissage) · source CAN brute préservée</p>
      </div>
    </div>
  );
}
